var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import config from '../../../config';
import { persistentKVStore } from '../persistence';
import { isCommissionShop, isRentalShop } from '../domain';
import { isAdminUser, isAgentUser, isBarberUser, isBrandUser, isShopUser } from './user-kinds';
var getActiveShopId = function () {
    return persistentKVStore.getItem('shop');
};
var getActiveBarber = function (_a) {
    var barbers = _a.barbers, activeShopId = _a.activeShopId;
    if (activeShopId && (barbers === null || barbers === void 0 ? void 0 : barbers.length)) {
        return barbers.find(function (barber) { var _a; return ((_a = barber.shop) === null || _a === void 0 ? void 0 : _a.id) === activeShopId; });
    }
    return barbers === null || barbers === void 0 ? void 0 : barbers[0];
};
var getActiveShop = function (user) {
    var localShopId = getActiveShopId();
    var shops = user === null || user === void 0 ? void 0 : user.shops;
    if (localShopId && (shops === null || shops === void 0 ? void 0 : shops.length)) {
        var targetShop = shops.find(function (shopItem) { return shopItem.id === localShopId; });
        if (targetShop) {
            return targetShop;
        }
    }
    return shops === null || shops === void 0 ? void 0 : shops[0];
};
var createBarberSession = function (user) {
    var activeShopId = getActiveShopId();
    var barber = getActiveBarber({ barbers: user.barbers, activeShopId: activeShopId });
    var shop = barber === null || barber === void 0 ? void 0 : barber.shop;
    if (!barber) {
        throw new Error('No barber specified!');
    }
    if (!shop) {
        throw new Error('No shop specified!');
    }
    var brand = shop.brand;
    var view = {
        kind: 'barber',
        viewer: barber,
        owners: {
            brand: brand,
            shop: shop,
        },
        meta: {
            indy: brand.shopCount === 1 && shop.barberCount === 1,
            commission: isCommissionShop(shop),
            rental: isRentalShop(shop),
            currency: shop.currency,
            timezone: shop.timezone,
            dateFormat: shop.dateFormat,
        },
        permissions: barber.permissions,
    };
    return {
        user: user,
        userKind: 'barber',
        view: view,
    };
};
var createShopSession = function (user) {
    var shop = getActiveShop(user);
    if (!shop) {
        throw new Error('No shop specified!');
    }
    var brand = shop.brand;
    var view = {
        kind: 'shop',
        viewer: shop,
        owners: { brand: brand },
        meta: {
            commission: isCommissionShop(shop),
            rental: isRentalShop(shop),
            currency: shop.currency,
            timezone: shop.timezone,
            dateFormat: shop.dateFormat,
        },
        permissions: shop.permissions,
    };
    return {
        user: user,
        userKind: 'shop',
        view: view,
    };
};
var createBrandSession = function (user) {
    var _a, _b, _c, _d, _e, _f;
    var sharedSession = {
        user: user,
        userKind: 'brand',
    };
    var activeShopId = getActiveShopId();
    // brand users can view both shops and brands
    // if there is a shop in localStorage, use it. Otherwise, use brand.
    if (activeShopId) {
        return createShopSession(user);
    }
    var brand = user.brand;
    if (!brand) {
        throw new Error('No brand specified!');
    }
    var timezone = (_c = (_b = (_a = user.shops) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.timezone) !== null && _c !== void 0 ? _c : config.defaultTimezone;
    var dateFormat = (_f = (_e = (_d = user.shops) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.dateFormat) !== null && _f !== void 0 ? _f : config.defaultDateFormat;
    var view = {
        kind: 'brand',
        viewer: brand,
        meta: {
            currency: brand.currency,
            timezone: timezone,
            dateFormat: dateFormat,
        },
        permissions: brand.brand_users.permissions,
    };
    return __assign(__assign({}, sharedSession), { view: view });
};
var createAdminSession = function (user) {
    return {
        user: user,
        userKind: 'admin',
        view: {
            kind: 'admin',
        },
    };
};
var createAgentSession = function (user) {
    return {
        user: user,
        userKind: 'agent',
        view: {
            kind: 'agent',
        },
    };
};
export var createSession = function (user) {
    if (isBarberUser(user)) {
        return createBarberSession(user);
    }
    if (isShopUser(user)) {
        return createShopSession(user);
    }
    if (isBrandUser(user)) {
        return createBrandSession(user);
    }
    if (isAdminUser(user)) {
        return createAdminSession(user);
    }
    if (isAgentUser(user)) {
        return createAgentSession(user);
    }
    throw new Error("No factory for user kind: ".concat(user.kind));
};
